import * as React from "react";
import { filter, groupBy, includes, map } from "lodash";
import { CircleAlert } from "lucide-react";
import { useQuery } from "@tanstack/react-query";

import * as DTO from "../../../interfaces/dtos";
import { userFullName, useDebounced, useSocialCoachSession } from "@/utils";
import { SearchFilter } from "@/interfaces";
import { SegmentationDetailServices } from "@/services/bedrock";
import { usePaginatedCombobox } from "@/utils";

import { AbbreviatedPopover } from "@/components";
import { DataTable, Checkbox, Tooltip, Alert, Combobox, Spinner, Input, Badge } from "@/components/ui";
import { ColumnDef } from "@tanstack/react-table";
import { useEffect, useMemo, useState } from "react";
import { PlayersServices } from "@/services";
import { cn } from "@/lib/utils";

interface OwnProps {
  selectedPlayers: number[];

  disabled?: boolean;
  showState?: boolean;
  onChangeSelectedPlayer: (playerIds: number[], checked: boolean) => void;

  /**
   * Search function name
   */
  segmentedPlayers?: number[];
  isInclude?: boolean;
  onLoadingChange?: (loading: boolean) => void;
  onPlayersLoaded?: (players: DTO.PlayerWithBranch[]) => void;
  queryKey?: string;
  scrollable?: boolean;
}

export function PlayersListWithBranch({
  selectedPlayers = [],
  showState = false,
  onChangeSelectedPlayer,
  disabled,
  segmentedPlayers,
  onLoadingChange,
  onPlayersLoaded,
  isInclude = true,
  queryKey: propsQueryKey,
  scrollable = false,
}: OwnProps) {
  const { coachId } = useSocialCoachSession();
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounced(searchTerm, 300);

  const getSegmentationDetails = React.useCallback((search: string | undefined, page: number) => {
    const types = ["STATE", "REGION", "BRANCH"];
    return SegmentationDetailServices.getSegmentationDetails(types, search, page, 25);
  }, []);
  const segmentationDetails = usePaginatedCombobox(getSegmentationDetails, true);

  const filterBody = useMemo(() => {
    const filter: SearchFilter = {};

    if (segmentationDetails.value) {
      const groups = groupBy(segmentationDetails.value, "type");
      if (groups["BRANCH"]) {
        filter.branchIds = map(groups["BRANCH"], "id");
      }
      if (groups["REGION"]) {
        filter.regionIds = map(groups["REGION"], "id");
      }
      if (groups["STATE"]) {
        filter.stateIds = map(groups["STATE"], "id");
      }
    }
    if (segmentedPlayers && segmentedPlayers.length > 0 && isInclude) {
      filter.playerIds = segmentedPlayers;
    }
    return filter;
  }, [segmentationDetails.value, segmentedPlayers, isInclude]);

  const { data, isLoading } = useQuery({
    queryKey: [propsQueryKey || "branchSearch", coachId, filterBody],
    queryFn: () => PlayersServices.searchSubscribedPlayersByTag(coachId!, filterBody),
    enabled: !!coachId,
  });

  const allPlayers = useMemo<DTO.PlayerWithBranch[]>(() => {
    if (!data) return [];
    let result = data.content;
    if (!isInclude) {
      result = filter(result, player => !includes(segmentedPlayers, player.playerAccount.playerId!));
    }

    return result.map(player => {
      return {
        info: player.playerAccount,
        photoUrl: player.playerAccount.photoUrl,
        email: player.playerAccount.email,
        branchNames: player.branchNames,
        regionNames: player.regionNames,
        stateCodes: player.stateCodes,
      } as DTO.PlayerWithBranch;
    });
  }, [data, isInclude, segmentedPlayers]);

  const players = useMemo(() => {
    if (!debouncedSearchTerm.trim()) return allPlayers;
    const lowerSearch = debouncedSearchTerm.toLowerCase();
    return allPlayers.filter(p => {
      const fullName = userFullName(p.info).toLowerCase();
      const email = p.email?.toLowerCase() || "";
      return fullName.includes(lowerSearch) || email.includes(lowerSearch);
    });
  }, [allPlayers, debouncedSearchTerm]);

  useEffect(() => {
    onPlayersLoaded && onPlayersLoaded(allPlayers);
  }, [allPlayers, onPlayersLoaded]);

  useEffect(() => {
    onLoadingChange && onLoadingChange(isLoading);
  }, [isLoading, onLoadingChange]);

  const hasRegions = filter(players, "regionNames").length > 0;
  const hasBranches = filter(players, "branchNames").length > 0;
  const hasStates = filter(players, "stateCodes").length > 0;

  const allPlayersSelected = players.length > 0 && players.every(p => selectedPlayers.includes(p.info!.playerId!));

  const columns = React.useMemo<ColumnDef<DTO.PlayerWithBranch>[]>(() => {
    const cols: ColumnDef<DTO.PlayerWithBranch>[] = [
      {
        id: "select",
        header: () => (
          <Checkbox
            checked={allPlayersSelected}
            className="bg-white"
            onCheckedChange={checked => {
              const allPlayersIds = players.map(p => p.info!.playerId!);
              onChangeSelectedPlayer(allPlayersIds, checked);
            }}
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={selectedPlayers.includes(row.original.info!.playerId!)}
            onCheckedChange={checked => {
              onChangeSelectedPlayer([row.original.info!.playerId!], checked);
            }}
          />
        ),
        enableSorting: false,
        meta: { intrinsic: true },
      },
      {
        accessorKey: "info.firstName",
        header: "Name",
        cell: ({ row }) => {
          const { info } = row.original;
          return (
            <div className="flex items-center gap-2">
              <AbbreviatedPopover text={userFullName(info)} maxLength={50} />
              {!info.socialNetworks?.length && (
                <Tooltip>
                  <Tooltip.Trigger>
                    <CircleAlert className="size-4 text-orange-500" />
                  </Tooltip.Trigger>
                  <Tooltip.Content>No social networks connected.</Tooltip.Content>
                </Tooltip>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: "email",
        header: "Email",
        cell: ({ row }) => <AbbreviatedPopover text={row.original.email} maxLength={50} />,
      },
    ];

    if (hasBranches) {
      cols.push({
        accessorKey: "branch",
        header: "Branch",
        cell: ({ row }) => <AbbreviatedPopover text={row.original.branchNames?.join(", ")} />,
      });
    }

    if (hasRegions) {
      cols.push({
        accessorKey: "region",
        header: "Region",
        cell: ({ row }) => <AbbreviatedPopover text={row.original.regionNames?.join(", ")} />,
      });
    }

    if (showState && hasStates) {
      cols.push({
        accessorKey: "state",
        header: "State",
        cell: ({ row }) => <AbbreviatedPopover text={row.original.stateCodes?.join(", ")} />,
      });
    }

    return cols;
  }, [players, selectedPlayers, onChangeSelectedPlayer, showState]);

  return (
    <div className={cn("flex flex-col gap-2", scrollable && "flex-1 min-h-0 h-full")}>
      <div className="flex flex-row gap-2 items-center">
        <Input
          placeholder="Search name or email..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className="max-w-xs bg-white"
        />
        <Combobox
          {...segmentationDetails}
          itemToStringLabel={segmentationDetail => segmentationDetail.name || "-"}
          itemToBadge={segmentationDetail => segmentationDetail.type}
          placeholder="Filter by Segmentation"
          className="min-w-100 bg-white"
        />

        <div className="flex-1" />

        <Badge variant="outline" className="h-9 px-3 gap-1.5 bg-white shadow-sm border-slate-200">
          <span className="text-slate-400 font-medium">Selected:</span>
          <span className="font-bold text-slate-900">{selectedPlayers.length}</span>
        </Badge>

        {isLoading && <Spinner />}
      </div>

      <div className={cn("bg-white", scrollable && "flex flex-col flex-1 min-h-0 h-full")}>
        {isLoading || players.length > 0 ? (
          <DataTable
            columns={columns}
            data={players}
            rowCount={players.length}
            loading={isLoading}
            disabled={disabled}
            scrollable={scrollable}
          />
        ) : (
          <Alert variant="default" className="mt-4">
            <Alert.Title>No results</Alert.Title>
            <Alert.Description>There are no players matching your search criteria.</Alert.Description>
          </Alert>
        )}
      </div>
    </div>
  );
}
